<script lang="ts" setup>
import {
  shallowRef,
  computed,
  nextTick,
  watch,
  type HTMLAttributes,
  type VNode,
  type ComponentPublicInstance,
} from 'vue';
import {
  useFloating,
  flip,
  autoUpdate,
  shift,
  offset,
  size,
  type Middleware,
} from '@floating-ui/vue';
import { onClickOutside, unrefElement, type MaybeElementRef } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useZIndex, useFocusScope } from '../../composables';
import { mergeRefs } from '../../utils';
import { ObScrollableContainer } from '../scrollable-container';
import { ObUnmountableTeleportWithTransition } from '../internal';

interface Props {
  open?: boolean;
  floatingMiddleware?: (middleware: Middleware[]) => Middleware[];
}

defineOptions({
  inheritAttrs: false,
});

const { floatingMiddleware } = defineProps<Props>();

const emit = defineEmits<{
  clickOutside: [event: PointerEvent];
  activated: [];
  deactivated: [];
}>();

type RefSetter = (
  el: MaybeElementRef | ComponentPublicInstance | Element | SVGElement | null,
) => void; // TODO: move to shared

defineSlots<{
  default?: (props: { activate: () => void; deactivate: () => void; toggle: () => void }) => VNode;
  host?: (props: {
    hostProps: HTMLAttributes & Record<string, any>;
    hostRef: RefSetter;
    open: boolean;
    mergeRefs: typeof mergeRefs;
  }) => VNode;
}>();

const open = defineModel<boolean>('open', { default: false });

const { zIndex } = useZIndex({ active: computed(() => open.value) });

const hostElement = shallowRef();
const containerRef = shallowRef();

const middleware = computed<Middleware[]>(() => {
  let result = [
    size({
      padding: 8,
      apply({ elements, availableHeight }) {
        Object.assign(elements.floating.style, {
          maxHeight: `${availableHeight}px`,
        });
      },
    }),
    offset(8),
    flip({
      padding: 8,
    }),
    shift({
      padding: 8,
    }),
  ];

  if (typeof floatingMiddleware === 'function') {
    result = floatingMiddleware(result);
  }

  return result;
});

const { floatingStyles } = useFloating(hostElement, containerRef, {
  placement: 'bottom-start',
  middleware,
  whileElementsMounted: autoUpdate,
  open,
  transform: false,
});

const { active: focusScopeActive } = useFocusScope(containerRef);

const { activate: activateTrap, deactivate: deactivateTrap } = useFocusTrap(containerRef, {
  escapeDeactivates: false,
  allowOutsideClick: true,
  clickOutsideDeactivates: false,
  returnFocusOnDeactivate: false,
  fallbackFocus: () => {
    return unrefElement(containerRef) ?? document.body;
  },
});

watch(
  open,
  (value) => {
    if (value) {
      nextTick(() => {
        activateTrap();
      });
      return;
    }

    deactivateTrap();
  },
  { immediate: true },
);

onClickOutside(
  containerRef,
  () => {
    setTimeout(() => {
      if (!focusScopeActive.value) {
        deactivate();
      }
    }, 1);
  },
  { ignore: [hostElement] },
);

function onEsc(event: KeyboardEvent) {
  event.preventDefault();
  event.stopPropagation();
  deactivate();
}

function onClick(event: MouseEvent) {
  event.preventDefault();
  toggle();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    activate();
  }
}

function activate() {
  open.value = true;
  emit('activated');
}

function deactivate() {
  open.value = false;
  emit('deactivated');
}

function toggle() {
  if (open.value) {
    deactivate();
    return;
  }

  activate();
}

defineExpose({
  activate,
  deactivate,
  toggle,
});

const hostRef: RefSetter = (el) => {
  hostElement.value = el ? unrefElement(el as MaybeElementRef) : null;
};
</script>

<template>
  <slot name="host" v-bind="{ hostProps: { onKeydown, onClick }, hostRef, mergeRefs, open }" />
  <ObUnmountableTeleportWithTransition
    :active="open"
    :enter-from-class="$style.enterFrom"
    :enter-active-class="$style.enterActive"
    :leave-active-class="$style.leaveActive"
    :leave-to-class="$style.leaveTo"
  >
    <div
      v-if="open"
      ref="containerRef"
      :style="{ ...floatingStyles, zIndex }"
      :class="$style.container"
      v-bind="$attrs"
      @keydown.esc="onEsc"
    >
      <ObScrollableContainer light overscroll-behavior="contain">
        <slot v-bind="{ activate, deactivate, toggle }" />
      </ObScrollableContainer>
    </div>
  </ObUnmountableTeleportWithTransition>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/shared';
@use '../../styles/typography';

.enterActive,
.leaveActive {
  transition-property: opacity, transform;
  transition-duration: 0.2s;
}

.enterActive {
  transition-timing-function: ease-out;
}

.leaveActive {
  transition-timing-function: ease-in;
}

.enterFrom,
.leaveTo {
  opacity: 0;
  transform: scale(0.9);
}

.container {
  box-sizing: border-box;
  height: auto;
  width: auto;
  border-radius: 12px;
  background: #fff;
  color: colors.$primary;
  border: 1px solid colors.$surface-6;
  box-shadow: 0px 0px 18px 0px rgba(2, 17, 72, 0.2);
  font-family: typography.$font-family-primary;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
